<template>
	<div>
		<b-overlay :show="isLoading" rounded="sm" no-wrap />
		<Card body-classes="p-3">
			<b-row align-v="center" slot="header">
				<b-col cols="8">
					<h3 class="mb-0">Leaderboard Question</h3>
				</b-col>
				<b-col cols="4" class="text-right">
					<BaseButton :disabled="!isUpdatedNeed" @click="updateLeaderboardQuestion" size="sm">Update Question
					</BaseButton>
				</b-col>
			</b-row>

			<b-row align-v="center" slot="footer">
				<h6 class="text-muted m-0 px-2"></h6>
			</b-row>

			<b-form>
				<div class="px-lg-4">
					<b-row>
						<b-col>
							<base-input type="text" label="Question" placeholder="Enter Question" name="questiontitle"
								v-model="updatedData.questionTitle">
							</base-input>
						</b-col>
					</b-row>
					<!---ANSWERS-->
					<b-row v-for="(option, index) in updatedData.options" :key="option.id"
						class="align-items-center mb-2">
						<b-col cols="7">
							<base-input type="text" :label="index === 0 ? 'Answers' : ''" placeholder="Enter Answer"
								v-model="option.text" @input="markDirty" />
						</b-col>
						<b-col cols="4">
							<BaseButton :type="option.id === correctAnswerId ? 'success' : 'primary'" block
								@click="setCorrectAnswer(option.id)">
								{{ option.id === correctAnswerId ? '✔ Correct' : 'Mark as Correct' }}
							</BaseButton>
						</b-col>
						<b-col cols="1">
							<BaseButton type="danger" block @click="deleteOption(index)">✕</BaseButton>
						</b-col>
					</b-row>


					<Card body-classes="p-3">
						<b-row align-v="center" slot="header">
							<b-col cols="8">
								<h3 class="mb-0">Add New Answer Option</h3>
							</b-col>
						</b-row>

						<b-row align-v="center">
							<b-col>
								<base-input type="text" label="New Answer Option" placeholder="Enter Answer option"
									name="answer option" v-model="newOptionText">
								</base-input>
							</b-col>
						</b-row>
						<b-row class="mb-2">
							<b-col cols="auto" class="mr-auto">
								<label class="form-control-label">Is Correct Answer?</label>
							</b-col>
							<b-col cols="auto">
								<BaseSwitch v-model="newOptionIsCorrect"></BaseSwitch>
							</b-col>
						</b-row>
						<b-row>
							<b-col>
								<BaseButton block @click="addNewOption">Add</BaseButton>
							</b-col>
						</b-row>
					</Card>
				</div>
			</b-form>
		</Card>
	</div>
</template>

<script>
import Card from '../../../components/Cards/Card.vue';
import BaseButton from '../../../components/BaseButton.vue';
import { mapActions, mapGetters } from 'vuex';
import { getOrganizationFirestore } from '../../../resources/organizationFirebase';
import { doc, setDoc } from '@firebase/firestore';
import BaseInput from '../../../components/Inputs/BaseInput.vue';
import BaseSwitch from '../../../components/BaseSwitch.vue';

export default {
	computed: {
		...mapGetters({
			isFullAdmin: 'isFullAdmin',
			organization: 'getOrganization',
		}),
	},
	props: {
		businessId: {
			type: String,
			default: null
		},
		leaderboardData: {
			type: Object,
			description: 'Leaderboard Information',
		},
	},
	components: { Card, BaseButton, BaseInput, BaseSwitch },
	data() {
		return {
			isLoading: false,
			isUpdatedNeed: false,
			updatedData: {
				questionTitle: null,
				options: [],
			},
			correctAnswerId: null,
			newOptionText: '',
			newOptionIsCorrect: false,
			originalData: null,
			defaultedLeaderboardData: null
		};
	},
	watch: {
		updatedData: {
			handler(val) {
				return (this.isUpdatedNeed = this.checkIfUpdateNeeded());
			},
			deep: true,
		},
		leaderboardData: {
			immediate: true,
			deep: true,
			handler() {
				this.setupView();
			}
		}
	},
	methods: {
		...mapActions(['showNotification']),
		markDirty() {
			this.isUpdatedNeed = this.checkIfUpdateNeeded();
		},
		setupView() {
			let data = this.leaderboardData;

			if (!data || Object.keys(data).length === 0) {
				data = {
					createdVia: 'adminPortal',
					leaderboardDescription: '', // will be set later from question title
					leaderboardTitle: 'Trivia Question',
					scanLimitPerUser: 1,
					leaderboardIsActive: true,
					leaderboardPoints: 30,
					leaderboardId: this.businessId || 'unknown_id',
					leaderboardIsBusiness: true,
				};
			}

			const question = (data.leaderboardQuestion || {});

			this.updatedData = {
				questionTitle: this.getValue(question.questionTitle) || '',
				options: JSON.parse(JSON.stringify(question.options || [])),
			};

			this.correctAnswerId = this.getValue(question.correctAnswerId) || null;

			this.originalData = {
				questionTitle: this.updatedData.questionTitle,
				options: JSON.parse(JSON.stringify(this.updatedData.options)),
				correctAnswerId: this.correctAnswerId
			};

			this.defaultedLeaderboardData = data;
		},
		setCorrectAnswer(id) {
			if (this.correctAnswerId !== id) {
				this.correctAnswerId = id;
				this.markDirty();
			}
		},
		deleteOption(index) {
			this.updatedData.options.splice(index, 1);
			this.markDirty();
		},
		addNewOption() {
			if (!this.newOptionText.trim()) return;

			if (this.updatedData.options.length >= 5) {
				this.showNotification({
				type: 'warning',
				message: 'You can only add up to 5 answer options.',
				});
				return;
			}

			const newId = 'opt_' + Math.random().toString(36).substr(2, 9);
			this.updatedData.options.push({ id: newId, text: this.newOptionText });

			if (this.newOptionIsCorrect) {
				this.correctAnswerId = newId;
			}

			this.newOptionText = '';
			this.newOptionIsCorrect = false;
			this.markDirty();
		},
		checkIfUpdateNeeded() {
			const original = this.originalData;
			const updated = this.updatedData;

			if (!original || !updated) return false;

			if (original.questionTitle !== updated.questionTitle) return true;
			if (original.correctAnswerId !== this.correctAnswerId) return true;

			if (original.options.length !== updated.options.length) return true;

			for (let i = 0; i < updated.options.length; i++) {
				if (
					original.options[i]?.id !== updated.options[i]?.id ||
					original.options[i]?.text !== updated.options[i]?.text
				) {
					return true;
				}
			}

			return false;
		},
		onNewAnswerSwitched() {
			//i need to pass the selected answer and them mark it as 'correctanswerid'
		},
		updateLeaderboardQuestion() {
			if (
				!this.updatedData.questionTitle ||
				this.updatedData.options.length < 2 ||
				!this.correctAnswerId
			) {
				this.showNotification({
					type: 'danger',
					message: 'Please enter a question title, at least two answers, and select a correct answer.',
				});
				return;
			}

			const baseData = this.defaultedLeaderboardData || {};

			const updatedPayload = {
				leaderboardItem: {
					...baseData,
					leaderboardDescription: this.updatedData.questionTitle,
					leaderboardQuestion: {
						...this.updatedData,
						correctAnswerId: this.correctAnswerId,
						questionId: baseData.leaderboardId || this.businessId
					}
				}
			};


			this.isLoading = true;
			const firestore = getOrganizationFirestore(this.organization);
			const ref = doc(
				firestore,
				`business/${this.businessId}`
			);
			setDoc(ref, updatedPayload, { merge: true })
				.then(() => {
					this.isLoading = false;
					this.isUpdatedNeed = false;

					this.$emit('updatedLeaderboard', this.updatedData);
					this.showNotification({
						type: 'success',
						message: 'Update Complete',
					});
				})
				.catch((error) => {
					this.isLoading = false;
					this.showNotification({
						type: 'danger',
						message: error.message,
					});
				});
		},
		getValue(value) {
			if (value === undefined || value === null || this.$helpers.isBlank(value)) {
				return null;
			}
			return value;
		},
	},
	mounted() {
		this.setupView();
	},
};
</script>
